import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  Card,
  Typography,
  Tag,
  Row,
  Col,
  Statistic,
  DatePicker,
  Select,
  Button,
  Space,
  Empty,
  Input,
} from "antd";
import { FileExcelOutlined, SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { AppContext } from "../context/AppContext";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Zambian colors
const ZAMBIAN_COLORS = {
  green: "#078930",
  red: "#EF3340",
  black: "#000000",
  orange: "#FFB81C",
};

const InvoicesPage = () => {
  const { company } = useContext(AppContext);
  const [loans, setLoans] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [combinedBorrowers, setCombinedBorrowers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: [moment().subtract(30, "days"), moment()],
    business: "all",
    searchText: "",
  });
  const token = localStorage.getItem("token");

  // Define columns with only the required fields
  const columns = [
    {
      title: "Ref",
      dataIndex: "employee_number",
      key: "ref",
      sorter: (a, b) =>
        (a.employee_number || "").localeCompare(b.employee_number || ""),
    },
    {
      title: "NRC",
      dataIndex: "nrc",
      key: "nrc",
      sorter: (a, b) => (a.nrc || "").localeCompare(b.nrc || ""),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      sorter: (a, b) => (a.department || "").localeCompare(b.department || ""),
      filters: [],
      onFilter: (value, record) => record.department === value,
    },
    {
      title: "Business",
      dataIndex: "business",
      key: "business",
      sorter: (a, b) => (a.business || "").localeCompare(b.business || ""),
      filters: [],
      onFilter: (value, record) => record.business === value,
    },
    {
      title: "Total Loan Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (amount) => (
        <Text
          strong
          style={{
            color: ZAMBIAN_COLORS.green,
          }}
        >
          {`ZMW ${amount ? amount.toLocaleString() : 0}`}
        </Text>
      ),
      sorter: (a, b) => a.totalAmount - b.totalAmount,
    },
  ];

  const exportToCSV = () => {
    const csvContent = [
      [
        "Ref",
        "NRC",
        "Name",
        "Department",
        "Business",
        "Total Loan Amount (ZMW)",
      ],
      ...combinedBorrowers.map((borrower) => [
        borrower.employee_number || "N/A",
        borrower.nrc || "N/A",
        borrower.name,
        borrower.department || "N/A",
        borrower.business || "N/A",
        borrower.totalAmount,
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `invoices_${moment().format("YYYYMMDD")}.csv`;
    link.click();
  };

  const fetchLoans = async () => {
    try {
      setLoading(true);

      // Build filter parameters for date range and company
      const filterParams = {
        company: company.id, // Ensure we're using the current company ID
        date_created: {
          _between: [
            filters.dateRange[0].format("YYYY-MM-DD"),
            filters.dateRange[1].format("YYYY-MM-DD"),
          ],
        },
      };

      // Fetch loans with borrower details
      const response = await axios.get(
        "https://zedlenders.pickmesms.com/items/loans",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            fields:
              "*,borrower.id,borrower.first_name,borrower.last_name,borrower.nrc,borrower.employee_number,borrower.department,borrower.business",
            filter: filterParams,
            sort: "-date_created",
          },
        }
      );

      // Process the loans data
      const loansData = response.data.data || [];
      setLoans(loansData);
      processLoans(loansData);
    } catch (error) {
      console.error("Error fetching loans:", error);
    } finally {
      setLoading(false);
    }
  };

  // Process loans to combine amounts for each borrower
  const processLoans = (loans) => {
    console.log("Fetched loans:", loans);
    const borrowersMap = new Map();

    // Group loans by borrower
    loans.forEach((loan) => {
      if (!loan.borrower) return;

      const borrowerId = loan.borrower.id;

      if (!borrowersMap.has(borrowerId)) {
        borrowersMap.set(borrowerId, {
          id: borrowerId,
          name: `${loan.borrower.first_name || ""} ${
            loan.borrower.last_name || ""
          }`.trim(),
          nrc: loan.borrower.nrc,
          employee_number: loan.borrower.employee_number,
          department: loan.borrower.department,
          business: loan.borrower.business,
          totalAmount: 0,
          loans: [],
        });
      }

      const borrower = borrowersMap.get(borrowerId);
      borrower.totalAmount += loan.amount || 0;
      borrower.loans.push(loan);
    });

    // Convert map to array - include only borrowers with loans
    const combinedBorrowers = Array.from(borrowersMap.values()).filter(
      (borrower) => borrower.loans.length > 0
    );

    setCombinedBorrowers(combinedBorrowers);
    applyFilters(combinedBorrowers);
  };

  // Apply filters to combined borrowers data
  const applyFilters = (data) => {
    let filtered = [...data];

    // Apply business filter if selected
    if (filters.business !== "all") {
      filtered = filtered.filter((item) => item.business === filters.business);
    }

    // Apply search text filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchLower) ||
          (item.nrc && item.nrc.toLowerCase().includes(searchLower)) ||
          (item.employee_number &&
            item.employee_number.toLowerCase().includes(searchLower)) ||
          (item.department &&
            item.department.toLowerCase().includes(searchLower)) ||
          (item.business && item.business.toLowerCase().includes(searchLower))
      );
    }

    setFilteredLoans(filtered);
  };

  useEffect(() => {
    if (company?.id) {
      fetchLoans();
    }
  }, [filters.dateRange, company?.id]);

  useEffect(() => {
    // When filters change, reapply filters to existing data without refetching
    applyFilters(combinedBorrowers);
  }, [filters.business, filters.searchText]);

  // Extract unique companies for the filter dropdown
  useEffect(() => {
    if (combinedBorrowers.length > 0) {
      // Update column filters for business and department
      const uniqueBusinesses = [
        ...new Set(
          combinedBorrowers.map((item) => item.business).filter(Boolean)
        ),
      ];
      const uniqueDepartments = [
        ...new Set(
          combinedBorrowers.map((item) => item.department).filter(Boolean)
        ),
      ];

      columns[3].filters = uniqueDepartments.map((dept) => ({
        text: dept,
        value: dept,
      }));
      columns[4].filters = uniqueBusinesses.map((business) => ({
        text: business,
        value: business,
      }));
    }
  }, [combinedBorrowers]);

  // Calculate summary statistics
  const getSummaryStats = () => {
    const totalLoans = filteredLoans.length;
    const totalAmount = filteredLoans.reduce(
      (sum, item) => sum + item.totalAmount,
      0
    );
    const averageLoanAmount = totalLoans > 0 ? totalAmount / totalLoans : 0;

    return {
      totalLoans,
      totalAmount,
      averageLoanAmount,
    };
  };

  const summary = getSummaryStats();

  // Handle search input
  const handleSearch = (value) => {
    setFilters({
      ...filters,
      searchText: value,
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title
              level={3}
              style={{ marginBottom: 0, color: ZAMBIAN_COLORS.green }}
            >
              Invoices Report
            </Title>
            <Text type="secondary">
              {company?.name} • Updated {moment().format("DD MMM YYYY")}
            </Text>
          </Col>
          <Col>
            <Button.Group>
              <Button
                icon={<FileExcelOutlined />}
                onClick={exportToCSV}
                style={{
                  borderColor: ZAMBIAN_COLORS.green,
                  color: ZAMBIAN_COLORS.green,
                }}
              >
                Export CSV
              </Button>
            </Button.Group>
          </Col>
        </Row>

        {/* Summary Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={8}>
            <Card bordered={false}>
              <Statistic
                title="Total Borrowers"
                value={summary.totalLoans}
                valueStyle={{ color: ZAMBIAN_COLORS.black }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card bordered={false}>
              <Statistic
                title="Total Loan Amount"
                value={summary.totalAmount}
                valueStyle={{ color: ZAMBIAN_COLORS.green }}
                suffix="ZMW"
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card bordered={false}>
              <Statistic
                title="Average Loan Amount"
                value={summary.averageLoanAmount}
                valueStyle={{ color: ZAMBIAN_COLORS.orange }}
                suffix="ZMW"
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={24} md={8} lg={8}>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Select
              placeholder="Filter by Business"
              defaultValue="all"
              style={{ width: "100%" }}
              onChange={(value) => setFilters({ ...filters, business: value })}
            >
              <Option value="all">All Businesses</Option>
              {[...new Set(combinedBorrowers.map((item) => item.business))]
                .filter(Boolean)
                .map((business) => (
                  <Option key={business} value={business}>
                    {business}
                  </Option>
                ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Input
              placeholder="Search by name, ref, nrc, etc."
              prefix={<SearchOutlined />}
              value={filters.searchText}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
            />
          </Col>
        </Row>

        {/* Invoices Table */}
        <Table
          columns={columns}
          dataSource={filteredLoans}
          rowKey="id"
          loading={loading}
          scroll={{ x: true }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} borrowers`,
          }}
        />
      </Card>
    </div>
  );
};

export default InvoicesPage;
